import { createContext, use, useCallback, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

type Slot = 'start' | 'end';

interface Slots {
  start: HTMLElement | null;
  end: HTMLElement | null;
  set: (slot: Slot, element: HTMLElement | null) => void;
}

const HeaderSlotsContext = createContext<Slots | null>(null);

/** Holds the two header slot elements so routes can fill the shell's header. */
export function HeaderSlotsProvider({ children }: { children: ReactNode }) {
  const [start, setStart] = useState<HTMLElement | null>(null);
  const [end, setEnd] = useState<HTMLElement | null>(null);
  const set = useCallback(
    (slot: Slot, element: HTMLElement | null) => (slot === 'start' ? setStart : setEnd)(element),
    [],
  );
  return <HeaderSlotsContext value={{ start, end, set }}>{children}</HeaderSlotsContext>;
}

/** The place in the header a slot renders. Empty slots take no space. */
export function HeaderSlotTarget({ slot, className }: { slot: Slot; className?: string }) {
  const set = use(HeaderSlotsContext)?.set;
  // Stable, so React does not detach and re-attach the slot on every render.
  const ref = useCallback(
    (element: HTMLDivElement | null) => {
      set?.(slot, element);
    },
    [set, slot],
  );
  return <div ref={ref} className={`contents${className ? ` ${className}` : ''}`} />;
}

function Fill({ slot, children }: { slot: Slot; children: ReactNode }) {
  const slots = use(HeaderSlotsContext);
  // No shell around this route (route tests): render where it stands.
  if (slots === null) return children;
  const target = slots[slot];
  return target === null ? null : createPortal(children, target);
}

/** Content for the header, after the organisation name. */
export function HeaderStart({ children }: { children: ReactNode }) {
  return <Fill slot="start">{children}</Fill>;
}

/** Content for the header, before the connection indicator. */
export function HeaderEnd({ children }: { children: ReactNode }) {
  return <Fill slot="end">{children}</Fill>;
}
